import { MAX_RESULTS } from '../constants';

/**
 * Scope §7 — reveal ranked matches in batches without disturbing the ranking.
 *
 * The ranking itself is computed elsewhere over the *whole* eligible set. Everything here is a
 * view over that finished list, which is what makes "Load More must not randomize or recompute
 * the ranking" true by construction rather than by discipline.
 */

/**
 * Results are revealed in batches of this size. Derived from MAX_RESULTS rather than restated,
 * so the batch the page adds and the batch this module reports can never drift apart.
 */
export const RESULTS_PER_PAGE = MAX_RESULTS;

export interface VisibleResults<T> {
  visiblePerfectMatches: T[];
  visibleCloseAlternatives: T[];
}

/**
 * Splits a reveal budget across the two result groups in their established order: Perfect Matches
 * are consumed first, and only what is left over reaches Close Alternatives.
 *
 * Groups are sliced separately rather than concatenated, so a result can never migrate between
 * categories as more are revealed (§7: "Load More must not merge, silently reclassify, or change
 * a result's existing match category").
 */
export function splitVisibleResults<T>(
  perfectMatches: readonly T[],
  closeAlternatives: readonly T[],
  visibleCount: number
): VisibleResults<T> {
  const budget = Math.max(0, visibleCount);
  return {
    visiblePerfectMatches: perfectMatches.slice(0, budget),
    visibleCloseAlternatives: closeAlternatives.slice(0, Math.max(0, budget - perfectMatches.length)),
  };
}

/** Suffix naming the relaxed-criteria group, so the eligible count is not read as covering it. */
export function formatCloseAlternativeCount(count: number): string {
  return `${count} close alternative${count === 1 ? '' : 's'}`;
}

/**
 * True while at least one result is still hidden. Takes the total being paged through — the
 * *revealable* set, which includes Close Alternatives, not the narrower eligible count.
 */
export function hasMoreResults(totalRevealable: number, visibleCount: number): boolean {
  return visibleCount < totalRevealable;
}

/** How many the next activation would reveal — never more than actually remain. */
export function nextBatchSize(totalRevealable: number, visibleCount: number): number {
  return Math.max(0, Math.min(RESULTS_PER_PAGE, totalRevealable - visibleCount));
}

/**
 * "27 eligible manufacturing partners", per the scope's example. Singular where it matters, since
 * "1 eligible manufacturing partners" reads as a bug to the sponsor.
 */
export function formatEligibleCount(totalEligible: number): string {
  return `${totalEligible} eligible manufacturing partner${totalEligible === 1 ? '' : 's'}`;
}

/** Gap between one card's animation and the next. */
const STAGGER_STEP_SECONDS = 0.06;

/**
 * Delay before the card at `index` animates in. Results arrive one batch at a time, so the stagger
 * is relative to the batch: card 5 — the first of the second page — starts at 0 just like card 0.
 *
 * Two failures this avoids. `index * 0.1` unbounded made the 150th card wait 14.9s, so a filter
 * change looked frozen. Clamping the *absolute* index instead fixed that but killed the stagger
 * everywhere past the first page: every card in every later batch landed on the same delay, which
 * showed as a dead beat and then five cards appearing at once. Taking the position within the
 * batch bounds the delay by the batch size and keeps every page staggered.
 */
export function staggerDelaySeconds(index: number): number {
  return (Math.max(0, index) % RESULTS_PER_PAGE) * STAGGER_STEP_SECONDS;
}
